// Generate: the pre-leads the agent found, kept, drafted and wrote to, from review to a reply worth a deal.
import {
  CanAccess,
  useGetList,
  useListContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { List } from "@/components/admin/list";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";

import { SimpleList } from "../../simple-list/SimpleList";
import { IthnkPage } from "../IthnkPage";
import { AddPreLeadDialog } from "./AddPreLeadDialog";
import { FindPreLeadsDialog } from "./FindPreLeadsDialog";
import { PipelineStrip } from "./PipelineStrip";
import { PreLeadDrawer } from "./PreLeadDrawer";
import { PreLeadNext, PreLeadStatusTag, PreLeadTable } from "./PreLeadTable";
import { SegmentResults } from "./SegmentResults";
import type { PreLead } from "./types";
import { isRemovable, usePreLeadActions } from "./usePreLeadActions";
import { usePreLeadKeys, type KeyAction } from "./usePreLeadKeys";

const Actions = () => (
  <div className="flex flex-wrap items-center gap-2">
    <AddPreLeadDialog />
    <FindPreLeadsDialog />
  </div>
);

/** The nightly lead run's counts (lead_agent_runs, latest night) */
const LastNight = () => {
  const translate = useTranslate();
  const { data } = useGetList("lead_agent_runs", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "day", order: "DESC" },
  });
  const run = data?.[0];
  if (!run) return null;
  return (
    <p className="pb-2 text-sm text-muted-foreground">
      {translate(
        run.finished_at
          ? "resources.pre_leads.last_night"
          : "resources.pre_leads.last_night_running",
        { found: run.found, drafted: run.drafted, sent: run.sent },
      )}
    </p>
  );
};

const PreLeadsBody = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const {
    data = [],
    total,
    isPending,
    filterValues,
  } = useListContext<PreLead>();
  // one current row: the keyboard moves it, the drawer shows it when open
  const [currentId, setCurrentId] = useState<Identifier | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const actions = usePreLeadActions();
  const current = data.find((p) => p.id === currentId);
  const open = (p: PreLead) => {
    setCurrentId(p.id);
    setDrawerOpen(true);
    setWide(p.needs_reply);
  };
  const move = (step: number) => {
    const at = data.findIndex((p) => p.id === currentId);
    const next = data[Math.min(data.length - 1, Math.max(0, at + step))];
    if (!next) return;
    if (drawerOpen) open(next);
    else setCurrentId(next.id);
  };

  usePreLeadKeys((action: KeyAction) => {
    if (actions.removing) return;
    if (action === "next") return move(1);
    if (action === "prev") return move(-1);
    if (action === "close") return setDrawerOpen(false);
    if (!current) return;
    if (action === "open") open(current);
    if (
      action === "keep" &&
      (current.status === "found" || current.status === "kept")
    )
      actions.keep([current]);
    if (action === "remove" && isRemovable(current))
      actions.askRemove([current]);
    if (action === "approve" && current.status === "ready")
      actions.approve([current]);
  });
  const empty = !isPending && !total && !Object.keys(filterValues).length;

  return (
    <>
      {actions.removeDialog}
      <LastNight />
      <PipelineStrip />
      {empty ? (
        <p className="py-8 text-center text-muted-foreground">
          {translate("resources.pre_leads.empty")}
        </p>
      ) : (
        <div className="flex gap-4">
          <div className="min-w-0 flex-1">
            {isMobile ? (
              <SimpleList<PreLead>
                rowClick={(_id, _resource, record) => {
                  open(record as PreLead);
                  return false;
                }}
                primaryText={(p) => p.company_name}
                secondaryText={(p) => <PreLeadStatusTag preLead={p} />}
                tertiaryText={(p) => <PreLeadNext preLead={p} />}
              />
            ) : (
              <PreLeadTable
                onOpen={open}
                currentId={currentId}
                compact={drawerOpen && !!current && wide}
              />
            )}
          </div>
          <PreLeadDrawer
            preLead={current}
            open={drawerOpen}
            wide={wide}
            onClose={() => setDrawerOpen(false)}
            onToggleWide={() => setWide(!wide)}
            onKeep={(p) => actions.keep([p])}
            onRemove={(p) => actions.askRemove([p])}
            onApprove={(p) => actions.approve([p])}
            working={actions.working}
          />
        </div>
      )}
    </>
  );
};

export const PreLeadsPage = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  return (
    <CanAccess resource="pre_leads" action="list">
      <IthnkPage title="resources.pre_leads.nav">
        <List
          resource="pre_leads"
          perPage={25}
          sort={{ field: "last_activity_at", order: "DESC" }}
          title={isMobile ? false : translate("resources.pre_leads.title")}
          disableBreadcrumb
          actions={<Actions />}
        >
          <Tabs defaultValue="pre_leads">
            <TabsList>
              <TabsTrigger value="pre_leads">
                {translate("resources.pre_leads.title")}
              </TabsTrigger>
              <TabsTrigger value="segments">
                {translate("resources.pre_leads.segment_results.title")}
              </TabsTrigger>
            </TabsList>
            {/* an inactive tab unmounts: the pre-leads keys only act while their tab is open */}
            <TabsContent value="pre_leads">
              <PreLeadsBody />
            </TabsContent>
            <TabsContent value="segments">
              <SegmentResults />
            </TabsContent>
          </Tabs>
        </List>
      </IthnkPage>
    </CanAccess>
  );
};

PreLeadsPage.path = "/generate";
